import React, { useState, useEffect } from 'react';
import { IceCreamCone } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TranslationProvider } from './contexts/TranslationContext';
import Layout from './components/Layout/Layout';
import LoginPage from './pages/LoginPage';
import ProduzionePage from './pages/ProduzionePage';
import GustiPage from './pages/GustiPage';
import PuntiVenditaPage from './pages/PuntiVenditaPage';
import FornitoriPage from './pages/FornitoriPage';
import StoricoProduzionePage from './pages/StoricoProduzionePage';
import CalendarioProduzionePage from './pages/CalendarioProduzionePage';
import IngredientiPage from './pages/IngredientiPage';
import SettingsPage from './pages/SettingsPage';
import UtentiPage from './pages/UtentiPage';
import AnalyticsPage from './pages/AnalyticsPage';
import DashboardPage from './pages/DashboardPage';
import LogAttivitaPage from './pages/LogAttivitaPage';
import StoricoEmailPage from './pages/StoricoEmailPage';
import ImpostazioniCategorieGustiPage from './pages/ImpostazioniCategorieGustiPage';
import ImpostazioniAllergeniPage from './pages/ImpostazioniAllergeniPage';
import ImpostazioniEmailPage from './pages/ImpostazioniEmailPage';
import ImpostazioniMeteoPage from './pages/ImpostazioniMeteoPage';
import ImpostazioniMenuPage from './pages/ImpostazioniMenuPage';
import ImpostazioniIntegrazioniPage from './pages/ImpostazioniIntegrazioniPage';
import CassaTicketPage from './pages/CassaTicketPage';
import KiaraImportsPage from './pages/KiaraImportsPage';
import KiaraDatiPage from './pages/KiaraDatiPage';
import VersamentoCassaPage from './pages/VersamentoCassaPage';
import TurnisticaPage from './pages/TurnisticaPage';
import ControlloGestionePage from './pages/ControlloGestionePage';
import PersonalePage from './pages/PersonalePage';
import BancoPage from './pages/BancoPage';
import MonitorPage from './pages/MonitorPage';
import ImpostazioniMonitorPage from './pages/ImpostazioniMonitorPage';
import AssistenzaPage from './pages/AssistenzaPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SuperAdminApp from './superadmin/SuperAdminApp';
import StampePage from './pages/StampePage';
import PrevisioneAnnualePage from './pages/PrevisioneAnnualePage';
import AnalisiPrevisioniPage from './pages/AnalisiPrevisioniPage';
import GiacenzaPage from './pages/GiacenzaPage';
import SmaltimentoPage from './pages/SmaltimentoPage';
import IncassoPage from './pages/IncassoPage';
import TabelleAnalisiPage from './pages/TabelleAnalisiPage';
import apiService from './services/api';
import './App.css';

// La dashboard è ora importata da ./pages/DashboardPage

// Componente principale dell'app
/**
 * Il monitor in gelateria non fa login e non ha menu: si riconosce
 * dall'indirizzo, e va servito prima di qualunque altra cosa — anche prima del
 * caricamento della sessione, che per uno schermo appeso al muro non esiste.
 */
const tokenMonitor = (): string | null => {
  const trovato = window.location.pathname.match(/^\/monitor\/([A-Za-z0-9_-]+)\/?$/);
  return trovato ? trovato[1] : null;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [monitor] = useState(tokenMonitor);

  // Una postazione e' il tablet al banco: la sua pagina e' il banco, non la
  // dashboard, che li' non serve a nessuno.
  useEffect(() => {
    if (user?.postazione) setCurrentPage('banco');
  }, [user]);

  // Gestisce navigazione basata su URL
  React.useEffect(() => {
    const handlePopstate = () => {
      const path = window.location.pathname;
      if (path === '/privacy-policy') {
        setCurrentPage('privacy-policy');
      } else if (path === '/terms-of-service') {
        setCurrentPage('terms-of-service');
      } else {
        setCurrentPage('dashboard');
      }
    };

    // Controlla URL iniziale
    handlePopstate();

    // Ascolta cambiamenti di URL
    window.addEventListener('popstate', handlePopstate);

    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, []);

  // Rendi la funzione di navigazione disponibile globalmente
  React.useEffect(() => {
    (window as any).onNavigate = (page: string) => {
      setCurrentPage(page);
      // Aggiorna URL se necessario
      if (page === 'privacy-policy') {
        window.history.pushState({}, '', '/privacy-policy');
      } else if (page === 'terms-of-service') {
        window.history.pushState({}, '', '/terms-of-service');
      } else if (window.location.pathname !== '/') {
        window.history.pushState({}, '', '/');
      }
    };

    return () => {
      delete (window as any).onNavigate;
    };
  }, [setCurrentPage]);

  // Tracciamento visualizzazione pagine
  useEffect(() => {
    if (isAuthenticated && currentPage) {
      apiService.trackPage(currentPage);
    }
  }, [currentPage, isAuthenticated]);

  if (monitor) {
    return <MonitorPage token={monitor} />;
  }

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-container">
          <div className="loading-icon"><IceCreamCone size={48} /></div>
          <div className="loading-text">Sistema Gelato</div>
          <p>Caricamento in corso...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Permetti accesso alle pagine legali anche senza autenticazione
    if (currentPage === 'privacy-policy') {
      return <PrivacyPolicyPage />;
    }
    if (currentPage === 'terms-of-service') {
      return <TermsOfServicePage />;
    }
    return <LoginPage />;
  }

  const renderCurrentPage = () => {

    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage onNavigate={setCurrentPage} />;
      case 'assistenza':
        return <AssistenzaPage />;
      case 'produzione':
        return <ProduzionePage onNavigate={setCurrentPage} />;
      case 'giacenza':
        return <GiacenzaPage onNavigate={setCurrentPage} />;
      case 'smaltimento':
        return <SmaltimentoPage onNavigate={setCurrentPage} />;
      case 'storico-produzione':
        return <StoricoProduzionePage />;
      case 'calendario-produzione':
        return <CalendarioProduzionePage onNavigate={setCurrentPage} />;
      case 'gusti':
        return <GustiPage />;
      case 'ingredienti':
        return <IngredientiPage />;
      case 'analytics':
        return <AnalyticsPage />;
      case 'users':
        return <UtentiPage />;
      case 'punti-vendita':
        return <PuntiVenditaPage />;
      case 'fornitori':
        return <FornitoriPage />;
      case 'settings':
        return <SettingsPage />;
      case 'stampe':
        return <StampePage />;
      case 'log-attivita':
        return <LogAttivitaPage />;
      case 'storico-email':
        return <StoricoEmailPage />;
      case 'impostazioni-menu':
        return <ImpostazioniMenuPage />;
      case 'impostazioni-integrazioni':
        return <ImpostazioniIntegrazioniPage />;
      case 'impostazioni-categorie-gusti':
        return <ImpostazioniCategorieGustiPage />;
      case 'impostazioni-allergeni':
        return <ImpostazioniAllergeniPage />;
      case 'impostazioni-email':
        return <ImpostazioniEmailPage />;
      case 'impostazioni-meteo':
        return <ImpostazioniMeteoPage />;
      case 'kiara-imports':
        return <KiaraImportsPage />;
      case 'kiara-dati':
        return <KiaraDatiPage />;
      case 'cassa-ticket':
        return <CassaTicketPage />;
      case 'versamento-cassa':
        return <VersamentoCassaPage />;
      case 'previsione-annuale':
        return <PrevisioneAnnualePage />;
      case 'analisi-previsioni':
        return <AnalisiPrevisioniPage />;
      case 'turni':
        return <TurnisticaPage />;
      case 'controllo-gestione':
        return <ControlloGestionePage />;
      case 'personale':
        return <PersonalePage />;
      case 'banco':
        return <BancoPage />;
      case 'impostazioni-monitor':
        return <ImpostazioniMonitorPage />;
      case 'privacy-policy':
        return <PrivacyPolicyPage />;
      case 'terms-of-service':
        return <TermsOfServicePage />;
      case 'incasso':
        return <IncassoPage />;
      case 'tabelle-analisi':
        return <TabelleAnalisiPage />;
      default:
        return <DashboardPage onNavigate={setCurrentPage} />;
    }
  };

  return (
    <Layout currentPage={currentPage} onNavigate={setCurrentPage}>
      {renderCurrentPage()}
    </Layout>
  );
};

const App: React.FC = () => {
  // Controlla se siamo nella sezione super admin
  const isSuperAdminRoute = window.location.pathname.startsWith('/superadmin');

  // Service Worker TEMPORANEAMENTE DISABILITATO per debug
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // DISABILITA E PULISCE tutti i Service Worker
    navigator.serviceWorker.getRegistrations?.().then((registrations) => {
      registrations.forEach((reg) => {
        console.log('[PWA] Unregistering Service Worker...');
        reg.unregister();
      });
    });
    
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((n) => {
          console.log('[PWA] Deleting cache:', n);
          caches.delete(n);
        });
      }).catch(() => { });
    }
    
    console.log('[PWA] Service Worker DISABILITATO (debug mode)');
  }, [isSuperAdminRoute]);

  if (isSuperAdminRoute) {
    // Renderizza l'app Super Admin separata
    return <SuperAdminApp />;
  }

  // Renderizza l'app normale
  return (
    <TranslationProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </TranslationProvider>
  );
};

export default App;
